import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { Table } from '@flipster/react/design-system-legacy/table';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { stack } from '@flipster/styled-system/patterns';
import {
  NonUsdCurrencyList,
  PusdtCurrency,
  TradingBonusUsdtCurrencies,
  UsdeCurrency,
  UsdtCurrency,
} from '@flipster/utils/constants/currency';
import Num from '@flipster/utils/num';

import { earnTradingBonusesBunja, getEarnTradingBonus } from '../../shared/earn/state';

interface Props {
  currency: string;
}
const TradingVolumeRewardTiersContent = ({ currency }: Props) => {
  const { earnTradingBonusesAtom } = useBunja(earnTradingBonusesBunja);
  const earnTradingBonuses = useAtomValue(earnTradingBonusesAtom);
  const { tradeBonusPeriod, balanceLimitTiers } = getEarnTradingBonus(currency, earnTradingBonuses);
  const displayCurrency = TradingBonusUsdtCurrencies.includes(currency) ? UsdtCurrency : currency;

  return (
    <div className={stack({ gap: 16 })}>
      {[PusdtCurrency, UsdtCurrency, UsdeCurrency].includes(currency) && (
        <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3' })}>
          The trading bonus limit is shared:
          <br />
          USDE first, then Premium Earn, then USDT
        </p>
      )}
      {NonUsdCurrencyList.includes(currency) && (
        <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3' })}>
          Trading volume includes both USDT and USD1 perpetual contracts
        </p>
      )}
      <div
        className={stack({
          textStyle: 'Text/L/Regular',
          color: 'content.default.level-4',
          border: '1px solid',
          borderColor: 'border.default.level-2',
          rounded: 8,
          overflow: 'hidden',
        })}
      >
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                css={{ ...tableHeaderCellStyle, ...tableBodyCellStyle, textAlign: 'right' }}
                justify='end'
              >
                {tradeBonusPeriod} day trading volume ({displayCurrency})
              </Table.HeaderCell>
              <Table.HeaderCell css={{ ...tableHeaderCellStyle, ...tableBodyCellStyle }} justify='end'>
                Bonus APR
              </Table.HeaderCell>
              <Table.HeaderCell css={{ ...tableHeaderCellStyle }} justify='end'>
                Earn limit ({displayCurrency})
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {balanceLimitTiers.map(({ tradeVolumeMin, apr, balanceCap }) => (
              <Table.Row key={tradeVolumeMin} css={{ ...tableRowStyle }}>
                <Table.BodyCell css={{ p: 12, ...tableBodyCellStyle }} justify='end'>
                  <Formatted.Number value={tradeVolumeMin} withComma css={{ color: 'content.default.level-2' }} />
                </Table.BodyCell>
                <Table.BodyCell css={{ p: 12, ...tableBodyCellStyle }} justify='end'>
                  <Formatted.Percent
                    value={Num(apr).multipliedBy(100)}
                    precision={2}
                    toFixed
                    css={{ whiteSpace: 'nowrap' }}
                  />
                </Table.BodyCell>
                <Table.BodyCell css={{ p: 12 }} justify='end'>
                  <Formatted.Number value={balanceCap} withComma />
                </Table.BodyCell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    </div>
  );
};

export default TradingVolumeRewardTiersContent;

const tableHeaderCellStyle = {
  py: 8,
  px: 12,
  textAlign: 'right',
  textStyle: 'Text/S/Strong',
  color: 'content.default.level-2',
};

const tableRowStyle = {
  '&:last-child': {
    borderBottom: 'none',
  },
};

const tableBodyCellStyle = {
  textAlign: 'right',
  borderRight: '1px solid',
  borderColor: 'border.default.level-1',
};
